import { useQuery } from "@tanstack/react-query";

import type { components } from "@/api/types";
import { apiGet, qk } from "@/shared/api";

export type EarningsResponse = components["schemas"]["EarningsResponse"];
export type EarningsPeriod = components["schemas"]["EarningsPeriod"];
export type RobotIncome = components["schemas"]["RobotIncome"];
export type AccrualItem = components["schemas"]["AccrualItem"];

/**
 * Как часто перезапрашивать главную. Начисление ежечасное, поэтому пяти минут
 * с запасом хватает, чтобы новый час появился сам, без переоткрытия экрана.
 *
 * Интервал стоит точечно, а не в дефолтах `QueryProvider`: rate limit бэкенда
 * считается ПО IP, и глобальный опрос всех ручек упёрся бы в 429 на всю
 * машину.
 */
const REFETCH_MS = 5 * 60_000;

/**
 * Заработок за период — данные главного экрана.
 *
 * Окно календарное (`day|week|month|all`) и НЕ совпадает с `ChartPeriod`
 * графика робота: «месяц» здесь означает месяц, а не «тридцать дней с шагом в
 * четыре часа». Поэтому и тип другой, и ключ кэша свой — перепутать их на
 * месте вызова не даст компилятор.
 *
 * Состояние экрана (`state`) считает бэкенд той же функцией, что и для
 * `/home`, — фронт его не пересчитывает (правило ТЗ №5).
 */
export function useEarnings(period: EarningsPeriod) {
  return useQuery({
    queryKey: qk.earnings(period),
    queryFn: ({ signal }) => apiGet("/api/v1/earnings", { query: { period }, signal }),
    refetchInterval: REFETCH_MS,
    // Пока грузится соседний период, показываем предыдущий ряд: иначе каждое
    // нажатие таба схлопывает карточку в скелет и экран прыгает.
    placeholderData: (previous) => previous,
  });
}
